import { useCallback, useEffect, useMemo } from 'react';
import { useCurrentWorkspace } from '../../../../hooks/useCurrentWorkspace';
import { useFilterConfigs } from '../../../../hooks/useFilterConfigs';
import { useFilters } from '../../../../hooks/useFilters';
import type { LookerDataType } from '../../../../lib/api/filterConfig';
import { type AdvancedFilterProviderState, useAdvancedFiltersStore } from '../../../../stores/advancedFilters';
import type { Relationship } from '../components/ConditionCard';
import type { Condition, Group } from '../sections/AdvancedFiltersSection';

export type ProviderType = 'global' | 'extend_with_facility_affiliation' | 'extend_with_care_team';

export const useAdvancedFilters = (hookProviderFor?: ProviderType) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { filterConfigs } = useFilterConfigs({
    disableAutoFetch: !currentWorkspace?.workspace_type_id,
  });
  const { setFilter } = useFilters();

  const store = useAdvancedFiltersStore();

  const { providers } = store;

  const providerState = hookProviderFor ? providers[hookProviderFor] : undefined;
  const groups = useMemo(() => (providerState?.groups as Group[]) ?? [], [providerState]);
  const relationshipBetweenGroups = useMemo(() => providerState?.relationshipBetweenGroups ?? 'AND', [providerState]);

  const getEffectiveProvider = useCallback(
    (functionProviderFor?: ProviderType) => {
      return functionProviderFor || hookProviderFor;
    },
    [hookProviderFor],
  );

  const addGroup = (condition?: Condition, provider?: ProviderType) => {
    const effectiveProvider = getEffectiveProvider(provider);
    if (effectiveProvider) {
      store.addGroup(effectiveProvider, condition);
    }
  };

  const removeGroup = useCallback(
    (groupId: number, provider?: ProviderType) => {
      const effectiveProvider = getEffectiveProvider(provider);
      if (effectiveProvider) {
        store.removeGroup(effectiveProvider, groupId);
      }
    },
    [store, getEffectiveProvider],
  );

  const addConditionToGroup = (groupId: number, condition: Condition, provider?: ProviderType) => {
    const effectiveProvider = getEffectiveProvider(provider);
    if (effectiveProvider) {
      store.addConditionToGroup(effectiveProvider, groupId, condition);
    }
  };

  const removeConditionFromGroup = (groupId: number, conditionName?: string, provider?: ProviderType) => {
    const effectiveProvider = getEffectiveProvider(provider);
    if (effectiveProvider) {
      store.removeConditionFromGroup(effectiveProvider, groupId, conditionName);
    }
  };

  const changeGroupRelationship = (groupId: number, relationship: Relationship, provider?: ProviderType) => {
    const effectiveProvider = getEffectiveProvider(provider);
    if (effectiveProvider) {
      store.changeGroupRelationship(effectiveProvider, groupId, relationship);
    }
  };

  const setRelationshipBetweenGroups = (relationship: Relationship, provider?: ProviderType) => {
    const effectiveProvider = getEffectiveProvider(provider);
    if (effectiveProvider) {
      store.setRelationshipBetweenGroups(effectiveProvider, relationship);
    }
  };

  const updateConditionValue = (groupId: number, conditionName: string, newValue: string, provider?: ProviderType) => {
    const effectiveProvider = getEffectiveProvider(provider);
    if (effectiveProvider) {
      store.updateConditionValue(effectiveProvider, groupId, conditionName, newValue);
    }
  };

  const updateConditionType = (groupId: number, conditionName: string, newType: string, provider?: ProviderType) => {
    const effectiveProvider = getEffectiveProvider(provider);
    if (effectiveProvider) {
      store.updateConditionType(effectiveProvider, groupId, conditionName, newType);
    }
  };

  const getConditionValue = (groupId: number, conditionName: string): string => {
    const group = groups.find((g) => g.id === groupId);
    const condition = group?.conditions.find((c) => c.name === conditionName);
    return condition?.value ?? '';
  };

  const clearAllGroups = (provider?: ProviderType) => {
    const effectiveProvider = getEffectiveProvider(provider);
    if (effectiveProvider) {
      store.clearAllGroups(effectiveProvider);
    }
  };

  const formatCondition = (condition: Condition): string => {
    if (!condition.value.length) return '';

    const expressionType = condition.conditionType ?? 'include';
    const dataType = condition.dataType ?? 'string';
    const formattedExpression = condition.value.replace(/'/g, '').replace(/\s*,\s*/g, ',');

    const name = condition.name.split('_filter')[0];

    return `|${name}#|${formattedExpression}#|${expressionType}#|${dataType}`;
  };

  const buildFilterString = useCallback((conditionGroups: Group[], globalRelationship: 'AND' | 'OR'): string => {
    const groupStrings = conditionGroups
      ?.map((group) => {
        const conditionStrings = group.conditions
          .map(formatCondition)
          .filter((str) => str !== '')
          .join('#|#');

        if (conditionStrings.length > 0) {
          return `${group.relationship}#|#${conditionStrings}`;
        }
        return '';
      })
      ?.filter((groupString) => groupString !== '');

    if (groupStrings.length <= 0) {
      return '';
    }
    return `${globalRelationship}#|#|#|#|${groupStrings.join('#|#|#|')}`;
  }, []);

  const parseConfigString = (
    inputConfigString: string,
  ): { groups: Group[]; relationshipBetweenGroups: 'AND' | 'OR' } => {
    if (!inputConfigString) {
      return {
        groups: [],
        relationshipBetweenGroups: 'AND',
      };
    }
    const configString = inputConfigString.slice(0, -1);
    const [globalRelationship, ...rest] = configString.split('#|#|#|#|');

    const restString = rest.join('#|#|#|#|');
    const pattern = /(?<=#\|)#\|(AND|OR)#\|/g;
    const parts = restString.split(pattern);
    const groupStrings = [parts[0]];
    for (let i = 1; i < parts.length; i += 2) {
      const delimiter = parts[i];
      const part = parts[i + 1];
      groupStrings.push(`${delimiter}#|${part}`);
    }

    const parsedGroups: Group[] = groupStrings.map((groupString, index) => {
      const [relationship, ...conditionParts] = groupString.split('#|').filter(Boolean);

      const conditions: Condition[] = [];
      for (let i = 0; i < conditionParts.length; i += 4) {
        const condition: Condition = {
          name: conditionParts[i],
          value: conditionParts[i + 1],
          friendlyName:
            filterConfigs.find((filter) => filter.filterName === `${conditionParts[i]}_filter`)?.friendlyName ||
            conditionParts[i],
          conditionType: conditionParts[i + 2],
          dataType: conditionParts[i + 3] as LookerDataType,
        };
        conditions.push(condition);
      }

      return {
        id: index + 1,
        conditions,
        relationship: relationship as Relationship,
      };
    });

    return {
      groups: parsedGroups,
      relationshipBetweenGroups: globalRelationship.substring(1) as 'AND' | 'OR',
    };
  };
  const setProviderState = (provider: ProviderType, state: AdvancedFilterProviderState) => {
    store.setProviderState(provider, state);
  };
  useEffect(() => {
    if (hookProviderFor) {
      store.initializeProvider(hookProviderFor);
    }
  }, [store, hookProviderFor]);

  useEffect(() => {
    if (hookProviderFor && groups?.every((g) => g.conditions.length === 0)) {
      groups?.forEach((group) => {
        removeGroup(group.id);
      });
    }
  }, [groups, removeGroup, hookProviderFor]);

  useEffect(() => {
    if (hookProviderFor && groups?.length && groups[0]?.conditions?.length) {
      const configString = buildFilterString(groups, relationshipBetweenGroups);
      let filterName = 'config_string';

      switch (hookProviderFor) {
        case 'extend_with_care_team':
          filterName = 'config_care_team_string';
          break;

        case 'extend_with_facility_affiliation':
          filterName = 'config_affiliated_graph_string';
          break;

        case 'global':
          filterName = 'config_string';
          break;
        default:
          filterName = 'config_string';
          break;
      }

      setFilter({
        filterName,
        expression: configString ? `"${configString}"` : '',
        enabled: true,
        expressionType: 'target',
      });
    }
  }, [groups, relationshipBetweenGroups, hookProviderFor, buildFilterString, setFilter]);

  return {
    groups,
    relationshipBetweenGroups,
    addGroup,
    removeGroup,
    addConditionToGroup,
    removeConditionFromGroup,
    changeGroupRelationship,
    setRelationshipBetweenGroups,
    updateConditionValue,
    getConditionValue,
    clearAllGroups,
    updateConditionType,
    buildFilterString,
    parseConfigString,
    setProviderState,
  };
};
